import { useState } from "react";
import { Wenn } from "@/auth/operations";
import { useNavigate } from "react-router-dom";
import { LayoutTemplate, Plus, RefreshCw } from "lucide-react";
import { useWorkspaceTemplates } from "@/api/queries/workspaceTemplates";
import { CreateTemplateDialog } from "@/components/scaffold-actions/CreateTemplateDialog";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// #290 (UC-2): Fach-Sicht „Workspace-Vorlagen" — die Entity-Ebene: WAS gibt es, WER nutzt es.
// Die Dateien/Freigaben zeigt das Detail über den geteilten RepoContentPanel-Baustein.
// Vokabular bewusst fachlich — kein ref/branch/sha auf dieser Seite.
export function WorkspaceTemplatesList() {
  const navigate = useNavigate();
  const templates = useWorkspaceTemplates();
  const [createOpen, setCreateOpen] = useState(false);

  const rows = templates.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-text3">
          {templates.isLoading ? "Lade…" : `${rows.length} Vorlage${rows.length === 1 ? "" : "n"}`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void templates.refetch()}>
            <RefreshCw className="h-4 w-4" /> Aktualisieren
          </Button>
          {/* #299 (Review M-2): der Knopf fuehrte fuer einen plugin-author sicher in ein
              403 — er sieht den Bereich (git-content:read), darf aber nicht anlegen. */}
          <Wenn op="workspace-template.create">
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="template-create">
              <Plus className="h-4 w-4" /> Vorlage anlegen
            </Button>
          </Wenn>
        </div>
      </div>

      <SectionCard noPad testId="templates-list">
        {templates.isError ? (
          <div className="p-[18px] text-[13px] text-danger">
            Vorlagen konnten nicht geladen werden: {(templates.error as Error | undefined)?.message}
          </div>
        ) : rows.length === 0 && !templates.isLoading ? (
          <div className="p-[18px] text-[13px] text-text3">
            Noch keine Workspace-Vorlagen. Eine Vorlage ist der Start-Inhalt für Agent-Workspaces —
            Settings-Profile verweisen darauf, neue Agents forken daraus.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vorlage</TableHead>
                <TableHead>Verwendet von</TableHead>
                <TableHead>Agents daraus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/workspace-templates/${encodeURIComponent(t.id)}`)}
                  data-testid={`template-row-${t.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <LayoutTemplate className="h-4 w-4 shrink-0 text-text3" />
                      <div>
                        <div className="font-semibold text-text">{t.name}</div>
                        <div className="mono text-[11px] text-text3">{t.repoFullName}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {t.usedByProfiles.length === 0 ? (
                      <span className="text-[12.5px] text-text3">kein Profil</span>
                    ) : (
                      <span className="text-[12.5px] text-text2">
                        {t.usedByProfiles.map((p) => p.name).join(", ")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-[12.5px] text-text2">{t.agentCount}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {createOpen && (
        <CreateTemplateDialog
          open
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/workspace-templates/${encodeURIComponent(id)}`)}
        />
      )}
    </div>
  );
}
